import { ReactNode, useMemo } from 'react';

import { getInteractiveContentType } from 'approot/activity-builder/activity-builder.utils';
import { LESSON_ACTIVITIES } from 'global/constants';
import { useCheckAnswer } from 'approot/activity-builder/check-answer/check-answer.hooks';
import { ViewProps } from './multiple-choice.constants';
import { checkAnswerVar } from 'approot/activity-builder/check-answer/check-answer.apollo';
import {
  getCheckAnswerTitle,
  isAnswerCorrect,
  resetComponentAnswer,
} from 'approot/activity-builder/check-answer/check-answer.utils';
import SubActivityView from '../sub-activity/sub-activity-view';
import {
  CheckAnswerButtonPrimary,
  CheckAnswerSecondaryButton,
} from 'approot/activity-builder/check-answer/check-answer.style';
import MultipleChoiceViewOptions from './multiple-choice-view-options';
import { useFindComponentDimensions } from '../component.hooks';
import ActivitySheet from '../shared/sheet/activity-sheet';
import ActivitySheetSaveStatus from '../shared/sheet/activity-sheet-save-status';
import { ClassroomAnswerInstruction } from '../../classroom/classroom-answer.style';
import ClassroomAnswerTabs, {
  CLASS_VIEW,
  INDIVIDUAL_VIEW,
  QUESTION_VIEW,
} from 'approot/activity-builder/classroom/classroom-answer-tabs';
import MultipleChoiceClassResponses from 'approot/activity-builder/classroom/multiple-choice/multiple-choice-class-responses';
import MultipleChoiceIndividualResponses from 'approot/activity-builder/classroom/multiple-choice/multiple-choice-individual-responses';
import * as Styled from './multiple-choice-check-answer.style';
import { useActivityBuilderFlags } from 'approot/activity-builder/activity-builder-flags';
import {
  ActivitySheetGridMax,
  ActivitySheetInnerContent,
  ActivitySheetInnerMax,
} from '../shared/sheet/activity-sheet.style';
import useInitialClassroomTab from 'approot/activity-builder/classroom/use-initial-classroom-tab';
import { LinkButton } from 'approot/shared/styles/buttons/link-button.style';

type Props = ViewProps & {
  isPublished: boolean;
  banner?: ReactNode;
  canClearModelledResponse?: boolean;
  onClose: () => void;
  onSubmitAnswer?: () => void;
};

const MultipleChoiceAnswerTeacher = (props: Props) => {
  const {
    isPublished,
    properties,
    subActivityProperties,
    id,
    type,
    onClose,
    onSubmitAnswer,
    canClearModelledResponse = true,
  } = props;

  const { activityBuilderRespondFlag } = useActivityBuilderFlags();
  const { tab, setTab } = useInitialClassroomTab({
    isVisible: true,
    canRespond: isPublished && activityBuilderRespondFlag,
    componentId: id,
  });

  const { checkAnswerData, currentAnswerData, hasAnswered } = useCheckAnswer({
    componentId: id,
  });
  const componentAnswers = checkAnswerData[id];

  const { width } = useFindComponentDimensions(id);

  const { multipleChoice } = properties;

  const isLesson = useMemo(
    () => getInteractiveContentType() === LESSON_ACTIVITIES,
    []
  );

  const selectedAnswers = componentAnswers?.multiAnswer?.answerRefs || [];

  const moreInformation = multipleChoice.moreInformation;

  const isOneOptionCorrect = useMemo(
    () =>
      multipleChoice.options.filter(o => o.correctAnswerBoolean).length === 1,
    [multipleChoice]
  );

  return (
    <ActivitySheet
      isVisible
      title="Multiple choice answers view modal"
      hasBackgroundColor={tab === QUESTION_VIEW}
      tabsContent={
        <ClassroomAnswerTabs
          isPublished={isPublished && activityBuilderRespondFlag}
          canClearModelledResponse={canClearModelledResponse}
          tab={tab}
          onSelect={option => setTab(option)}
          componentId={id}
          onCloseActivitySheet={() => {
            setTab(INDIVIDUAL_VIEW);
            onClose();
          }}
        />
      }
      banner={props.banner}
      customActions={
        <ActivitySheetSaveStatus
          title="Multiple choice response"
          showStatus={tab === QUESTION_VIEW}
          onClose={() => {
            setTab(INDIVIDUAL_VIEW);
            onClose();
          }}
        />
      }
    >
      <ActivitySheetInnerContent id="content">
        {tab === QUESTION_VIEW && (
          <ActivitySheetInnerMax>
            <ClassroomAnswerInstruction>
              <SubActivityView
                withTTSButton
                componentId={id}
                componentType={type}
                subActivityDetails={subActivityProperties}
              >
                {!isOneOptionCorrect && (
                  <Styled.MultipleChoiceCheckAnswerText>
                    You can select more than one.
                  </Styled.MultipleChoiceCheckAnswerText>
                )}

                <MultipleChoiceViewOptions
                  {...props}
                  isResponsive
                  showAnswer={isLesson}
                  width={width}
                />

                {isLesson && hasAnswered && currentAnswerData && (
                  <Styled.MultipleChoiceCheckAnswerFeedback>
                    {getCheckAnswerTitle(currentAnswerData)}
                  </Styled.MultipleChoiceCheckAnswerFeedback>
                )}

                {hasAnswered && !isLesson && (
                  <Styled.MultipleChoiceFreeAnswerText>
                    Your answer has been submitted.{' '}
                    <LinkButton
                      type="button"
                      $overrideFont
                      onClick={() => resetComponentAnswer(id, checkAnswerData)}
                    >
                      Change Answer
                    </LinkButton>
                  </Styled.MultipleChoiceFreeAnswerText>
                )}

                {isLesson &&
                  hasAnswered &&
                  currentAnswerData &&
                  !isAnswerCorrect(currentAnswerData) &&
                  moreInformation && (
                    <Styled.MultipleChoiceMoreInfo>
                      {moreInformation}
                    </Styled.MultipleChoiceMoreInfo>
                  )}
              </SubActivityView>
              <Styled.MultipleChoiceCheckAnswer>
                {hasAnswered &&
                currentAnswerData &&
                isLesson &&
                !isAnswerCorrect(currentAnswerData) ? (
                  <CheckAnswerSecondaryButton
                    type="button"
                    onClick={() => {
                      resetComponentAnswer(id, checkAnswerData);
                    }}
                  >
                    Try Again
                  </CheckAnswerSecondaryButton>
                ) : !hasAnswered ? (
                  <CheckAnswerButtonPrimary
                    type="button"
                    disabled={selectedAnswers.length === 0}
                    onClick={() => {
                      if (componentAnswers) {
                        checkAnswerVar({
                          ...checkAnswerData,
                          [id]: {
                            ...componentAnswers,
                            hasChecked: true,
                          },
                        });
                        onSubmitAnswer?.();
                      }
                    }}
                  >
                    Submit Answer
                  </CheckAnswerButtonPrimary>
                ) : null}
              </Styled.MultipleChoiceCheckAnswer>
            </ClassroomAnswerInstruction>
          </ActivitySheetInnerMax>
        )}
        {tab === CLASS_VIEW && (
          <ActivitySheetInnerMax>
            <MultipleChoiceClassResponses
              component={props}
              subActivityProperties={subActivityProperties}
            />
          </ActivitySheetInnerMax>
        )}
        {tab === INDIVIDUAL_VIEW && (
          <ActivitySheetGridMax>
            <MultipleChoiceIndividualResponses
              componentId={id}
              component={props}
            />
          </ActivitySheetGridMax>
        )}
      </ActivitySheetInnerContent>
    </ActivitySheet>
  );
};

export default MultipleChoiceAnswerTeacher;
